import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Activity, CheckCircle2, Filter, LogOut, RefreshCcw, Search, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trackLeadEvent } from "@/lib/leadTracking";
import { AsoEngineAdminPanel } from "@/components/equinox/aso-engine/AsoEngineAdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const leadStatuses = [
  { value: "all", label: "All stages" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "call_booked", label: "Call Booked" },
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const badgeClasses = {
  new: "border-blue-300/25 bg-blue-400/12 text-blue-100",
  contacted: "border-white/20 bg-white/8 text-white",
  call_booked: "border-emerald-300/25 bg-emerald-400/12 text-emerald-100",
  proposal_sent: "border-amber-300/25 bg-amber-400/12 text-amber-100",
  won: "border-green-300/25 bg-green-400/12 text-green-100",
  lost: "border-rose-300/25 bg-rose-400/12 text-rose-100",
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="glitter-card glass-panel rounded-[1.7rem] p-5" data-testid={testId}>
    <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/[0.1] bg-white/[0.06] text-blue-100">
      <Icon className="h-5 w-5" />
    </div>
    <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-white/[0.42]">{label}</p>
    <p className="mt-3 font-display text-4xl text-white">{value}</p>
  </div>
);

export default function LeadsDashboardPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingLeadId, setSavingLeadId] = useState("");
  const [notes, setNotes] = useState({});

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [overviewResult, leadsResult] = await Promise.allSettled([
        axios.get(`${API}/leads-dashboard/overview`, { withCredentials: true }),
        axios.get(`${API}/call-requests`, {
          withCredentials: true,
          params: {
            status: statusFilter === "all" ? "" : statusFilter,
            search,
          },
        }),
      ]);

      if (overviewResult.status === "fulfilled") {
        setOverview(overviewResult.value.data);
      }

      if (leadsResult.status === "fulfilled") {
        setLeads(leadsResult.value.data);
        setNotes(
          Object.fromEntries(
            leadsResult.value.data.map((lead) => [lead.id, lead.status_note || ""]),
          ),
        );
      }

      if (overviewResult.status === "rejected" && leadsResult.status === "rejected") {
        toast.error("Unable to load lead dashboard right now.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to load lead dashboard right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    trackLeadEvent("lead_dashboard_view", {
      label: "leads_dashboard_opened",
      metadata: { page: "leads_dashboard" },
    });
  }, [statusFilter]);

  const updateLeadStatus = async (leadId, leadStatus) => {
    try {
      setSavingLeadId(leadId);
      await axios.put(`${API}/call-requests/${leadId}/status`, {
        lead_status: leadStatus,
        status_note: notes[leadId] || "",
      }, { withCredentials: true });
      toast.success("Lead status updated.");
      fetchDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to update lead status.");
    } finally {
      setSavingLeadId("");
    }
  };

  const logoutDashboard = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("Signed out from lead tracker.");
      navigate("/lead-tracker-login");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to sign out right now.");
    }
  };

  const summaryCards = useMemo(
    () => [
      { label: "Total leads", value: overview?.total_leads ?? 0, icon: Target, testId: "leads-total-card" },
      { label: "New leads", value: overview?.new_leads ?? 0, icon: Activity, testId: "leads-new-card" },
      { label: "Active pipeline", value: overview?.active_pipeline ?? 0, icon: RefreshCcw, testId: "leads-active-card" },
      { label: "Won deals", value: overview?.won_leads ?? 0, icon: CheckCircle2, testId: "leads-won-card" },
    ],
    [overview],
  );

  return (
    <section className="px-6 pb-20 pt-16 sm:px-8 lg:px-12 lg:pb-28 lg:pt-20" data-testid="leads-dashboard-page">
      <div className="mx-auto max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-4xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="section-kicker">Lead Tracker</p>
              <h1 className="section-heading mt-5 max-w-[12ch] text-white">Track campaigns, attribution, and every high-intent lead.</h1>
              <p className="section-copy mt-6 text-white/[0.64]">
                This internal pipeline view is built to help you see which pages, campaigns, and outreach routes are producing qualified demand.
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={logoutDashboard}
              className="rounded-full border-white/[0.12] bg-white/[0.04] px-5 py-3 text-white hover:bg-white/[0.08]"
              data-testid="leads-dashboard-logout-button"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </motion.div>

        <Tabs defaultValue="core-leads" className="mt-10" data-testid="leads-dashboard-tabs">
          <TabsList className="h-auto rounded-full border border-white/[0.08] bg-white/[0.04] p-1">
            <TabsTrigger value="core-leads" className="rounded-full px-4 py-2 text-white data-[state=active]:bg-white data-[state=active]:text-black" data-testid="leads-tab-core">
              Core Leads
            </TabsTrigger>
            <TabsTrigger value="aso-engine" className="rounded-full px-4 py-2 text-white data-[state=active]:bg-white data-[state=active]:text-black" data-testid="leads-tab-aso-engine">
              ASO AI Engine
            </TabsTrigger>
          </TabsList>

          <TabsContent value="core-leads">
            <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {summaryCards.map((card) => (
                <StatCard key={card.label} {...card} />
              ))}
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-[0.82fr_1.18fr]">
          <div className="editorial-shell glitter-card glass-panel rounded-[2rem] p-6" data-testid="leads-dashboard-insights-card">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/[0.1] bg-white/[0.06] text-blue-100">
                <Filter className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">Filters & reporting</p>
                <h2 className="mt-1 font-display text-2xl text-white">Fast insight layer</h2>
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-white/[0.42]">Lead stage</p>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="leads-status-filter" className="h-11 rounded-2xl border-white/[0.08] bg-black/20 text-white">
                    <SelectValue placeholder="Filter by lead stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadStatuses.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.2em] text-white/[0.42]">Search</p>
                <div className="flex gap-3">
                  <div className="flex flex-1 items-center gap-2 rounded-2xl border border-white/[0.08] bg-black/20 px-4">
                    <Search className="h-4 w-4 text-white/[0.4]" />
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search name, email, company, campaign"
                      className="h-11 w-full bg-transparent text-sm text-white outline-none placeholder:text-white/[0.38]"
                      data-testid="leads-search-input"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={fetchDashboard}
                    className="h-11 rounded-2xl border-white/[0.12] bg-white/[0.04] text-white hover:bg-white/[0.08]"
                    data-testid="leads-search-button"
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/[0.42]">Top lead sources</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(overview?.by_source || []).slice(0, 5).map((item) => (
                    <Badge key={item.label} className="border-white/[0.1] bg-white/[0.06] px-3 py-2 text-white/[0.78]" data-testid={`leads-source-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {item.label} · {item.count}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/[0.42]">Top campaigns</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(overview?.recent_campaigns || []).length ? (
                    overview.recent_campaigns.map((item) => (
                      <Badge key={item.label} className="border-blue-300/20 bg-blue-400/10 px-3 py-2 text-blue-100" data-testid={`leads-campaign-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {item.label} · {item.count}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-white/[0.56]">Campaigns will show here once ad traffic starts using UTMs.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="glitter-card glass-panel rounded-[2rem] p-6" data-testid="leads-dashboard-table-card">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.42]">Lead pipeline</p>
                <h2 className="mt-2 font-display text-2xl text-white">Recent captured leads</h2>
              </div>
              <Badge className="border-white/[0.1] bg-white/[0.06] px-3 py-2 text-white/[0.72]" data-testid="leads-total-badge">
                {loading ? "Loading..." : `${leads.length} visible leads`}
              </Badge>
            </div>

            <div className="mt-6 overflow-hidden rounded-[1.5rem] border border-white/[0.08] bg-black/20">
              <Table data-testid="leads-dashboard-table" className="text-white">
                <TableHeader>
                  <TableRow className="border-white/[0.08] hover:bg-transparent">
                    <TableHead className="px-4 py-4 text-white/[0.42]">Lead</TableHead>
                    <TableHead className="px-4 py-4 text-white/[0.42]">Source</TableHead>
                    <TableHead className="px-4 py-4 text-white/[0.42]">Campaign</TableHead>
                    <TableHead className="px-4 py-4 text-white/[0.42]">Status</TableHead>
                    <TableHead className="px-4 py-4 text-white/[0.42]">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="px-4 py-8 text-center text-white/[0.55]">
                        Loading lead tracker...
                      </TableCell>
                    </TableRow>
                  ) : leads.length ? (
                    leads.map((lead) => (
                      <TableRow key={lead.id} className="border-white/[0.08] align-top hover:bg-white/[0.03]" data-testid={`lead-row-${lead.id}`}>
                        <TableCell className="px-4 py-4">
                          <div>
                            <p className="font-medium text-white">{lead.name}</p>
                            <p className="mt-1 text-sm text-white/[0.58]">{lead.email}</p>
                            <p className="mt-2 text-sm text-white/[0.72]">{lead.company}</p>
                            <p className="mt-2 line-clamp-2 text-sm text-white/[0.56]">{lead.goal}</p>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-4 text-sm text-white/[0.72]">
                          <p>{lead.source.replace(/_/g, " ")}</p>
                          <p className="mt-2 text-xs uppercase tracking-[0.2em] text-white/[0.4]">{lead.page_path || "direct"}</p>
                        </TableCell>
                        <TableCell className="px-4 py-4 text-sm text-white/[0.72]">
                          <p>{lead.attribution?.utm_campaign || "—"}</p>
                          <p className="mt-2 text-xs text-white/[0.44]">
                            {lead.attribution?.utm_source || "organic"}
                            {lead.attribution?.utm_medium ? ` / ${lead.attribution.utm_medium}` : ""}
                          </p>
                          <p className="mt-2 text-xs text-white/[0.44]">
                            {lead.attribution?.gclid ? "Google click ID saved" : lead.attribution?.fbclid ? "Meta click ID saved" : "No click ID"}
                          </p>
                        </TableCell>
                        <TableCell className="px-4 py-4">
                          <div className="space-y-3">
                            <Badge className={`${badgeClasses[lead.lead_status]} border px-3 py-2`} data-testid={`lead-status-badge-${lead.id}`}>
                              {lead.lead_status.replace(/_/g, " ")}
                            </Badge>
                            <Select value={lead.lead_status} onValueChange={(value) => updateLeadStatus(lead.id, value)}>
                              <SelectTrigger className="h-10 rounded-xl border-white/[0.08] bg-black/20 text-white" data-testid={`lead-status-select-${lead.id}`}>
                                <SelectValue placeholder="Update status" />
                              </SelectTrigger>
                              <SelectContent>
                                {leadStatuses
                                  .filter((status) => status.value !== "all")
                                  .map((status) => (
                                    <SelectItem key={status.value} value={status.value}>
                                      {status.label}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            {savingLeadId === lead.id ? <p className="text-xs text-cyan-200">Saving...</p> : null}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-4">
                          <Textarea
                            value={notes[lead.id] || ""}
                            onChange={(event) => setNotes((current) => ({ ...current, [lead.id]: event.target.value }))}
                            className="min-h-[108px] rounded-[1rem] border-white/[0.08] bg-black/20 text-white placeholder:text-white/[0.38]"
                            placeholder="Add quick notes before updating stage"
                            data-testid={`lead-note-input-${lead.id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="px-4 py-8 text-center text-white/[0.55]">
                        No leads match the current filters yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

            </div>
          </TabsContent>

          <TabsContent value="aso-engine">
            <div className="mt-10">
              <AsoEngineAdminPanel />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}